import { useMemo, useRef, useState } from "react";
import { useTradeFlowData } from "../../../context/TradeFlowDataContext ";
import { useTradeFlowTracker } from "../../../context/TradeFlowTrackerContext";
import { MentionEditorHandle } from "../../Shared/MentionInput";
import { ArrowRightIcon, CheckIcon, CrossIcon } from "../../Icons";
import {
  AiInstructionsRes,
  useAddAiInstructionsMutation,
  useDeleteAiInstructionsMutation,
  useGetAiInstructionsQuery,
} from "../../../store/apiSlices/aiAutomate";
import {
  AIAutomateTabContext,
  useAIAutomateTab,
} from "../../../context/AIAutomateTabContext";
import { toast } from "react-toastify";
import { TEMPLATE_GROUPS } from "../../../constants/aiAutomateSuggestions";
import AIAutomateMentionEditor from "../../Shared/AIAutomateMentionEditor";

const STEP_WIDTH = 200;

type TemplateGroupKey = keyof typeof TEMPLATE_GROUPS;

export default function AIAutomate() {
  const { tradeFlowData } = useTradeFlowData();
  const { tableContext } = useTradeFlowTracker();

  const { isLoading } = useGetAiInstructionsQuery({
    table_id: tableContext.table_id,
    row_id: tradeFlowData.row_id,
  });

  if (isLoading)
    return (
      <div className="animate-pulse h-full bg-slate-300 rounded-xl mb-8" />
    );

  return (
    <AIAutomate.Root>
      <AIAutomate.Flow />
      <div className="sticky bottom-0 bg-white py-4 border-t">
        <div className="max-w-xl mx-auto space-y-2">
          <AIAutomate.Editor />
          <AIAutomate.Templates />
          <AIAutomate.Topics />
        </div>
      </div>
    </AIAutomate.Root>
  );
}

AIAutomate.Root = function ({ children }: { children: React.ReactNode }) {
  const editorRef = useRef<MentionEditorHandle>(null);
  const [hasContent, setHasContent] = useState(false);
  const [activeTopic, setActiveTopic] = useState<TemplateGroupKey | null>(null);

  return (
    <AIAutomateTabContext.Provider
      value={{
        editorRef,
        hasContent,
        setHasContent,
        activeTopic,
        setActiveTopic,
      }}
    >
      <section>{children}</section>
    </AIAutomateTabContext.Provider>
  );
};

AIAutomate.Flow = function () {
  const { tradeFlowData } = useTradeFlowData();
  const { tableContext } = useTradeFlowTracker();

  const { data: allAiInstructions } = useGetAiInstructionsQuery({
    table_id: tableContext.table_id,
    row_id: tradeFlowData.row_id,
  });
  const [deleteAiInstructions] = useDeleteAiInstructionsMutation();

  const completedSteps =
    allAiInstructions?.data.filter((el) => el.is_completed).length || 0;
  const completedConnectors = Math.max(0, completedSteps - 1);

  const progressWidthPx = completedConnectors * STEP_WIDTH;

  const stepOrderMap = useMemo(() => {
    const map: Record<string, number> = {};

    tradeFlowData?.trade_flow_json?.forEach((s, index) => {
      map[s.step.field] = index;
    });

    return map;
  }, [tradeFlowData]);

  const groupedByStep = (allAiInstructions?.data ?? []).reduce(
    (acc, item) => {
      const step = item.step ?? "__UNKNOWN_STEP__";

      if (!acc[step]) {
        acc[step] = [];
      }

      acc[step].push(item);

      return acc;
    },
    {} as Record<string, AiInstructionsRes["data"]>,
  );

  const orderedSteps = useMemo(() => {
    return Object.entries(groupedByStep).sort(([stepA], [stepB]) => {
      const orderA = stepOrderMap[stepA] ?? Number.MAX_SAFE_INTEGER;

      const orderB = stepOrderMap[stepB] ?? Number.MAX_SAFE_INTEGER;

      return orderA - orderB;
    });
  }, [groupedByStep, stepOrderMap]);

  const handleDeleteTask = async (id: number) => {
    await deleteAiInstructions({
      ai_task_id: id,
      row_id: tradeFlowData.row_id,
      table_id: tableContext.table_id,
    });
  };

  if (allAiInstructions && allAiInstructions.data.length === 0)
    return (
      <p className="text-gray-500 text-center text-sm my-2">No AI task found</p>
    );

  return (
    <div className="relative w-full overflow-x-auto">
      <div
        className="relative flex items-start gap-3 text-xs"
        style={{ width: Object.keys(groupedByStep).length * STEP_WIDTH }}
      >
        {/* Background line */}
        <div
          className="absolute top-[16px] h-[2px] bg-gray-300"
          style={{
            left: STEP_WIDTH / 2,
            right: STEP_WIDTH / 2,
          }}
        />

        {/* Progress line */}
        <div
          className="absolute top-[16px] h-[2px] bg-gradient-to-r from-blue-800 to-blue-400 transition-all duration-300"
          style={{
            left: STEP_WIDTH / 2,
            width: progressWidthPx,
          }}
        />

        {/* Steps */}
        <div className="relative flex items-start h-[200px]">
          {orderedSteps.map(([step, tasks], idx) => {
            const isCompleted = tasks.every((t) => t.is_completed === 1);

            return (
              <div
                key={step}
                className="flex flex-col items-center text-center"
                style={{ width: STEP_WIDTH }}
              >
                <div
                  className={`size-8 flex items-center justify-center rounded-full border-2 font-semibold z-10
                  ${
                    isCompleted
                      ? "bg-blue-900 text-white"
                      : "bg-white text-gray-900 border-gray-400"
                  }`}
                >
                  {idx + 1}
                </div>

                <p className="mt-2 text-xs font-medium text-gray-800">
                  {step === "__UNKNOWN_STEP__" ? (
                    <span className="inline-block h-3 w-10 rounded bg-gray-300 animate-pulse" />
                  ) : (
                    step
                  )}
                </p>

                <div className="h-5 border border-gray-400 border-dashed" />

                <div className="space-y-2 w-[90%] mx-auto">
                  {tasks.map((task) => (
                    <div
                      key={task.id}
                      className="relative p-2 bg-gray-100 space-y-2 border shadow-sm rounded-xl"
                    >
                      <p
                        className="text-gray-700"
                        dangerouslySetInnerHTML={{
                          __html: task.instructions,
                        }}
                      />

                      <div>
                        {!task.is_completed && (
                          <button
                            className="absolute -right-1 -top-1 bg-red-400 rounded-full text-white hover:scale-90"
                            onClick={() => handleDeleteTask(task.id)}
                          >
                            <CrossIcon className="size-4" />
                          </button>
                        )}
                        {task.is_completed === 1 && (
                          <div className="absolute -right-1 -top-1 bg-green-400 rounded-full text-white p-[2px]">
                            <CheckIcon className="size-3" />
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

AIAutomate.Editor = function () {
  const { editorRef, hasContent, setHasContent } = useAIAutomateTab();
  const { tradeFlowData } = useTradeFlowData();
  const { tableContext } = useTradeFlowTracker();
  const [addAiInstructions] = useAddAiInstructionsMutation();

  const { trade_flow_json, row_id } = tradeFlowData;

  const allContacts =
    trade_flow_json
      .flatMap((step) => step.Contacts)
      .filter((c) => c.name?.trim() && c.email?.trim()) || [];

  const allDocuments =
    trade_flow_json
      .flatMap((step) => step.document_vault)
      .filter((d) => d.document_name.trim()) || [];

  const milestones = trade_flow_json.map((step) => step.step) || [];

  const onSend = async () => {
    const input = editorRef.current?.getValue() || "";

    const payload = {
      table_id: tableContext.table_id,
      row_id,
      instructions: input,
      is_completed: 0,
    };

    editorRef.current?.clear();
    setHasContent(false);

    const { data } = await addAiInstructions(payload);

    if (data.status === "fail") {
      toast.warning(data.message);
    }
  };

  return (
    <div className="relative">
      <AIAutomateMentionEditor
        ref={editorRef}
        contacts={allContacts}
        documents={allDocuments}
        milestones={milestones}
        id="instructions-input"
        placeholder="Give AI instructions. Type @ to tag persons , mention emails or dates."
        onInput={() => {
          const value = editorRef.current?.getValue() || "";
          setHasContent(!!value.trim());
        }}
        className="pr-10"
      />
      <button
        onClick={onSend}
        disabled={!hasContent}
        className={`absolute right-1 bottom-1 rounded-full p-1 font-medium transition-all shrink-0 ${
          hasContent
            ? "bg-black hover:bg-black/70 text-white"
            : "bg-gray-200 text-gray-400 cursor-not-allowed"
        }`}
      >
        <ArrowRightIcon className="size-6" />
      </button>
    </div>
  );
};

AIAutomate.Templates = function () {
  const { activeTopic, editorRef, setHasContent, setActiveTopic } =
    useAIAutomateTab();

  const handleOnSelect = (template: string) => {
    editorRef.current?.setValue(template);
    editorRef.current?.focus();
    setHasContent(true);
    setActiveTopic(null);
  };

  if (!activeTopic) return null;

  return (
    <div className="mt-2 rounded-lg border border-gray-200 bg-white shadow-sm">
      <ul className="divide-y text-xs">
        {TEMPLATE_GROUPS[activeTopic].templates.map((template, idx) => (
          <li key={idx}>
            <button
              type="button"
              className="w-full inline-flex gap-1 items-center text-left px-3 py-2 hover:bg-gray-100 transition"
              onClick={() => handleOnSelect(template)}
            >
              <ArrowRightIcon className="size-3 text-gray-500" />
              {template}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

AIAutomate.Topics = function () {
  const { activeTopic, setActiveTopic } = useAIAutomateTab();

  return (
    <>
      {/* Topic buttons */}
      <div className="flex flex-wrap gap-2">
        {Object.entries(TEMPLATE_GROUPS).map(([key, group]) => (
          <button
            key={key}
            type="button"
            className={`px-3 py-1.5 text-xs rounded-full border transition
                    ${
                      activeTopic === key
                        ? "bg-gray-900 text-white border-gray-900"
                        : "border-gray-300 text-gray-700 hover:bg-gray-100"
                    }`}
            onClick={() => setActiveTopic(key as TemplateGroupKey)}
          >
            {group.label}
          </button>
        ))}
      </div>

      {/* Helper note */}
      <div className="rounded-full w-full bg-gray-100 px-3 py-2 text-xs text-gray-600 inline-flex gap-1 items-center">
        <svg
          stroke="currentColor"
          fill="none"
          strokeWidth={2}
          viewBox="0 0 24 24"
          strokeLinecap="round"
          strokeLinejoin="round"
          height={200}
          width={200}
          xmlns="http://www.w3.org/2000/svg"
          className="size-4"
        >
          <path d="M3 12h1m8-9v1m8 8h1M5.6 5.6l.7.7m12.1-.7-.7.7M9 16a5 5 0 1 1 6 0 3.5 3.5 0 0 0-1 3 2 2 0 0 1-4 0 3.5 3.5 0 0 0-1-3m.7 1h4.6" />
        </svg>
        Select a specific topic above to get quick suggestions for instructions.
      </div>
    </>
  );
};
